/**
 * Contrato da aba Monitoramento de `/admin/emails` com a API
 * (`apps/api/src/modulos/comunicacao`, rota
 * `bff/v1/admin/comunicacao/emails`). É o contrato do banco: a situação
 * vem da fila de envio, e o endereço do destinatário chega inteiro.
 */

export const PERIODOS = ['24h', '7d', '30d'] as const;
export type Periodo = (typeof PERIODOS)[number];

export const ROTULO_DO_PERIODO: Record<Periodo, string> = {
  '24h': '24h',
  '7d': '7 dias',
  '30d': '30 dias',
};

export const SITUACOES_DO_ENVIO = ['NA_FILA', 'EM_ENVIO', 'ENVIADO', 'FALHOU', 'RETIDO'] as const;
export type SituacaoDoEnvio = (typeof SITUACOES_DO_ENVIO)[number];

export const ROTULO_DA_SITUACAO: Record<SituacaoDoEnvio, string> = {
  NA_FILA: 'Na fila',
  EM_ENVIO: 'Em envio',
  ENVIADO: 'Enviado',
  FALHOU: 'Falhou',
  /** Fora da regra de destino, em ambiente sem caixa de testes (HML): não saiu. */
  RETIDO: 'Retido',
};

export interface EmailEnviado {
  id: string;
  /** Código do evento que gerou o e-mail. */
  evento: string;
  destinatarioNome: string | null;
  /** Inteiro: a administração é área logada, sem máscara. */
  destinatarioEmail: string | null;
  situacao: SituacaoDoEnvio;
  criadoEm: string;
  enviadoEm: string | null;
  tentativas: number;
  erro: string | null;
}

export interface ResumoDosEnvios {
  total: number;
  naFila: number;
  enviados: number;
  falhas: number;
}

export interface ListaDeEmails {
  itens: EmailEnviado[];
  total: number;
  pagina: number;
  tamanho: number;
  periodo: Periodo;
  resumo: ResumoDosEnvios;
  eventos: string[];
}

export interface FiltroDeEmails {
  periodo: Periodo;
  situacao: string;
  evento: string;
  busca: string;
  pagina: number;
  tamanho: number;
}

/**
 * Nome, em português, de cada evento que gera e-mail. A lista é a das
 * mensagens da jornada (`catalogo.ts` da API) e dos modelos semeados nas
 * migrações; evento que ainda não esteja aqui aparece pelo código, em
 * palavras, em vez de sumir.
 */
const ROTULO_DO_EVENTO: Record<string, string> = {
  INSCRITO_CODIGO_VERIFICACAO: 'Código de verificação',
  INSCRITO_SENHA_REDEFINIDA: 'Senha redefinida',
  INSCRICAO_ENVIADA: 'Inscrição enviada',
  INSCRICAO_STATUS_ALTERADO: 'Situação da inscrição alterada',
  INSCRICAO_DEVOLVIDA_CORRECAO: 'Inscrição devolvida para correção',
  INSCRICAO_INDEFERIDA: 'Inscrição indeferida',
  INSCRICAO_CORRECAO_EXPIRADA: 'Prazo de correção expirado',
  INSCRICAO_PRE_CREDENCIADA: 'Inscrição pré-credenciada',
  INSCRICAO_EXPIRADA: 'Inscrição expirada',
  INSCRICAO_PENDENTE_DOCS: 'Documentação pendente',
  INSCRICAO_APROVADA: 'Inscrição aprovada',
  COOPERATIVA_INDICADA: 'Cooperativa indicada',
  COOPERATIVA_INDICACAO_RECEBIDA: 'Indicação de cooperativa recebida',
  SORTEIO_CONTEMPLADO: 'Sorteio: contemplado',
  SORTEIO_LISTA_ESPERA: 'Sorteio: lista de espera',
  SUPLENTE_CHAMADO: 'Suplente chamado',
  PRE_CREDENCIADO_ACESSO: 'Acesso do pré-credenciado',
  CONVOCACAO: 'Convocação',
  CREDENCIAMENTO_CONVOCADO: 'Credenciado convocado',
  HABILITACAO: 'Habilitação',
  CREDENCIAMENTO_HABILITADO: 'Credenciado habilitado',
  OS_EMITIDA: 'Ordem de serviço emitida',
  OS_ATIVA: 'Ordem de serviço ativa',
};

export function rotuloDoEvento(codigo: string): string {
  const conhecido = ROTULO_DO_EVENTO[codigo];
  if (conhecido) return conhecido;
  const palavras = codigo.toLowerCase().replace(/_/g, ' ');
  return palavras.charAt(0).toUpperCase() + palavras.slice(1);
}
